import { useState } from 'react';
import { Formik } from 'formik';
import {
  FormikWizardProvider,
  Wizard,
  StepsList,
  Step,
  ButtonsList,
  PreviousButton,
  NextButton,
  SubmitButton,
} from 'formik-wizard-form';
import { useQueryClient } from 'react-query';
import PropTypes from 'prop-types';

import { sendWorkReqeust, uploadBookingDetailsImage } from 'api';

import Button from 'components/ui-kit/buttons/button';
import SentRequestConfirmationBlock from 'components/sent-request-confirmation-block';

import { useApp, useModal, useToast } from 'hooks';
import usePageRestore from 'hooks/usePageRestore';

import {
  getSubmitLabel, isDepositDue, readDepositToken, takeSubmitAnswer,
} from './deposit';
import getSteps from './steps';
import stepsValidation from './stepsValidation';
import { buildFormikValuesEntries } from './utils';

import validationSchema from './schema';

import * as S from './styles';

const RequestWorkForm = ({ form }) => {
  const queryClient = useQueryClient();
  const { hideModal } = useModal();
  const { portal, user } = useApp();
  const { addErrorToast } = useToast();
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [isKeptWithoutDeposit, setIsKeptWithoutDeposit] = useState(false);
  // The page is on its way to the payment page: the submit stays locked until it has gone.
  const [isRedirecting, setIsRedirecting] = useState(false);

  // Back from the payment page can bring this page back as it was left, with the submit still locked.
  usePageRestore(() => setIsRedirecting(false));

  const isServicesEnabled = form?.services?.length > 0;

  const getCustomFields = () => form?.fields.filter(({ isEditable }) => isEditable);

  const getInitialValues = () => {
    const customFieldsDefaultValues = buildFormikValuesEntries(getCustomFields().map((field) => ({ question: field })));

    return {
      date: '',
      customTimeRangeEnabled: !!form.customTimeRange,
      servicesEnabled: isServicesEnabled,
      portalId: portal.id,
      time: '',
      serviceIds: [],
      address: '',
      geoData: '',
      unitNumber: '',
      isAddressInArea: '',
      email: !user.email ? '' : undefined,
      ...customFieldsDefaultValues,
    };
  };

  const getValidatorsWithCustomFields = () => {
    const bookingDetailsStepIndex = isServicesEnabled ? 1 : 0;
    const validators = stepsValidation;
    const customFieldsNames = getCustomFields().map(({ name }) => name);

    const BookingDetailsStep = ({ errors }) => {
      const errorsArray = customFieldsNames.map((name) => errors[name]);

      return !errorsArray.some((error) => error);
    };

    const ModifiedContactDetailsStep = ({ errors }) => (
      !errors.address
      && !errors.unitNumber
      && !errors.date
      && !errors.time
      && (!user.email ? !errors.email : true)
    );

    validators[bookingDetailsStepIndex] = BookingDetailsStep;
    validators[2] = ModifiedContactDetailsStep;

    return validators;
  };

  const submit = async (values) => {
    const customFieldNames = getCustomFields()
      .map(({ name, labelText }) => { return { name, labelText }; });

    const customAnswers = await Promise.all(
      customFieldNames.map(async ({ name, labelText }) => {
        const value = values[name];

        if (value instanceof File) {
          const { data: { imageUrl } } = await uploadBookingDetailsImage(portal.id, value);

          return {
            name,
            label: labelText,
            answers: [imageUrl],
          };
        }

        return {
          name,
          label: labelText,
          answers: Array.isArray(value) ? value.filter(Boolean) : [value],
        };
      }),
    );

    const selectedDate = values.date ? new Date(values.date) : null;
    // The token of the payment page an earlier try left open, if any: the one this submit may give up.
    const sentToken = readDepositToken(portal.id);

    const data = {
      serviceIds: values.serviceIds || [],
      time: values.time,
      date: selectedDate
        ? new Date(Date.UTC(selectedDate.getFullYear(), selectedDate.getMonth(), selectedDate.getDate(), 0, 0, 0))
        : null,
      customFields: customAnswers,
      address: {
        location: values.address,
        unitNumber: values.unitNumber,
        geoData: {
          latitude: Number(values.geoData.latitude),
          longitude: Number(values.geoData.longitude),
          placeId: values.geoData.placeId,
        },
      },
      ...(!user.email && values.email ? { email: values.email } : {}),
      // This page knows the answer of a booking that takes a deposit; the token is the earlier payment page's, if any.
      canPayDeposit: true,
      previousDepositToken: sentToken || undefined,
    };

    try {
      const result = await sendWorkReqeust(portal.id, data);
      const answer = takeSubmitAnswer(portal.id, sentToken, result.data);

      if (answer.unsafe) {
        addErrorToast('Something went wrong during sending the work request. You can try again later.');

        return;
      }

      if (answer.checkoutUrl) {
        setIsRedirecting(true);
        window.location.assign(answer.checkoutUrl);

        return;
      }

      if (!user.email && values.email) {
        queryClient.invalidateQueries(['current']);
      }

      // A payment was promised and none was asked for (the services come to nothing, the company can no longer take
      // cards): the request is saved without it, and the screen says so.
      setIsKeptWithoutDeposit(isDepositDue(form, values) || !!result.data?.keptWithoutDeposit);
      setShowConfirmation(true);
    } catch (error) {
      // The server says why when it can: the time was just taken, a question is unanswered.
      addErrorToast(
        error?.response?.data?.message
        || 'Something went wrong during sending the work request. You can try again later.',
      );
    }
  };

  return (
    <S.Content>
      {!showConfirmation && (
        <Formik
          enableReinitialize
          validateOnChange
          initialValues={getInitialValues()}
          validationSchema={validationSchema(getCustomFields(), form.showDateTimePicker, form.requireDateTime, user)}
          onSubmit={submit}
        >
          {(formikProps) => (
            <FormikWizardProvider {...formikProps}>
              {({ getValidators, ...restProps }) => (
                <Wizard {...restProps} className="wizard">
                  <StepsList validators={getValidators(getValidatorsWithCustomFields())}>
                    {getSteps(formikProps?.values?.servicesEnabled).map((step, i) => (
                      <Step
                        key={`step-${String(i)}`}
                        component={step.component}
                        title={step.title}
                        form={form}
                      />
                    ))}
                  </StepsList>

                  <ButtonsList>
                    <PreviousButton>
                      <Button w={90} h={48}>Back</Button>
                    </PreviousButton>

                    <NextButton>
                      <S.StyledNext
                        w={90}
                        h={48}
                        color="primary"
                        disabled={!restProps.isStepValid}
                      >
                        Next
                      </S.StyledNext>
                    </NextButton>

                    <SubmitButton>
                      <S.Button>
                        <Button
                          w={90}
                          h={48}
                          type="submit"
                          color="primary"
                          stretched
                          isLoading={formikProps.isSubmitting || isRedirecting}
                        >
                          {getSubmitLabel(form, formikProps.values)}
                        </Button>
                      </S.Button>
                    </SubmitButton>
                  </ButtonsList>
                </Wizard>
              )}
            </FormikWizardProvider>
          )}
        </Formik>
      )}

      {showConfirmation && (
        <SentRequestConfirmationBlock
          onSubmit={() => { hideModal(); }}
          note={isKeptWithoutDeposit ? 'No deposit was taken.' : null}
        />
      )}

      <S.Logo>
        <S.Text>Powered by</S.Text>

        <S.LogoImage />
      </S.Logo>
    </S.Content>
  );
};

RequestWorkForm.propTypes = {
  form: PropTypes.shape({
    fields: PropTypes.arrayOf(PropTypes.shape({})),
    services: PropTypes.arrayOf(PropTypes.shape({})),
    customTimeRange: PropTypes.number,
    workingSchedule: PropTypes.arrayOf(PropTypes.shape({})),
    showDateTimePicker: PropTypes.bool,
    requireDateTime: PropTypes.bool,
    deposit: PropTypes.shape({
      isEnabled: PropTypes.bool,
      amount: PropTypes.number,
    }),
  }).isRequired,
};

export default RequestWorkForm;
